import { useMemo, useState } from 'react';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import type { IconType } from 'react-icons';
import {
    PiArrowLeftBold,
    PiArrowRightBold,
    PiArrowDownBold,
    PiCaretRightBold,
    PiChartBarFill,
    PiCheckBold,
    PiClockCounterClockwiseBold,
    PiFileTextBold,
    PiGearBold,
    PiInfoBold,
    PiLightbulbBold,
    PiMagnifyingGlassBold,
    PiShieldCheckBold,
    PiUsersBold,
    PiWarningBold,
    PiWarningCircleBold,
    PiX,
    PiCalendarCheckBold,
} from 'react-icons/pi';
import { cn } from '@v2/utils/cn';
import { useI18n } from '@v2/providers/I18nProvider';
import { formatBrl } from '../utils';

export type CommissionActionGroup = 'sale' | 'installments' | 'wallet' | 'adjust' | 'rules' | 'legacy';

export type CommissionActionGuideKey = keyof typeof GUIDES;

export type CommissionActionItem = {
    key: CommissionActionGuideKey;
    group: CommissionActionGroup;
    icon: IconType;
    title: string;
    tone?: 'danger';
    disabled?: boolean;
    onRun: () => void;
};

type Guide = {
    kind: 'review' | 'inform';
    list: number;
    risks: number;
    side: 'risks' | 'example' | 'none';
    before?: boolean;
    tip?: 'hold';
};

// Estrutura de cada explicação; os textos ficam em v2.deal_actions.guide.<chave> (4 idiomas).
const GUIDES = {
    cancel: { kind: 'inform', list: 4, risks: 4, side: 'risks', tip: 'hold' },
    return: { kind: 'inform', list: 2, risks: 3, side: 'risks' },
    transfer: { kind: 'inform', list: 3, risks: 3, side: 'risks' },
    hold: { kind: 'review', list: 3, risks: 0, side: 'none' },
    reschedule: { kind: 'review', list: 3, risks: 0, side: 'example', before: true },
    repair: { kind: 'review', list: 3, risks: 0, side: 'none', before: true },
    shift: { kind: 'inform', list: 3, risks: 0, side: 'none' },
    wallet: { kind: 'inform', list: 3, risks: 3, side: 'risks' },
    edit: { kind: 'review', list: 3, risks: 2, side: 'risks' },
    realign: { kind: 'review', list: 3, risks: 3, side: 'risks' },
    bonus: { kind: 'inform', list: 1, risks: 0, side: 'none' },
    seller: { kind: 'inform', list: 2, risks: 0, side: 'none' },
    ta: { kind: 'inform', list: 3, risks: 0, side: 'none' },
    sdr: { kind: 'inform', list: 3, risks: 0, side: 'none' },
    cascade: { kind: 'review', list: 3, risks: 0, side: 'none' },
    slices: { kind: 'review', list: 2, risks: 0, side: 'none' },
    unfreeze: { kind: 'inform', list: 1, risks: 0, side: 'none' },
    refreeze: { kind: 'inform', list: 1, risks: 0, side: 'none' },
    realign_legacy: { kind: 'inform', list: 1, risks: 0, side: 'none' },
} satisfies Record<string, Guide>;

const COLUMNS: CommissionActionGroup[][] = [['sale'], ['installments', 'wallet', 'adjust'], ['rules', 'legacy']];

const G = 'v2.deal_actions.guide';

type Props = {
    open: boolean;
    onClose: () => void;
    items: CommissionActionItem[];
    onHistory?: () => void;
};

export default function CommissionActionsModal({ open, onClose, items, onHistory }: Props) {
    const { t } = useI18n();
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState<CommissionActionGuideKey | null>(null);

    const close = () => {
        onClose();
        setSelected(null);
        setSearch('');
    };
    const run = (item: CommissionActionItem) => {
        if (item.disabled) return;
        close();
        item.onRun();
    };

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return items;
        return items.filter((item) =>
            `${item.title} ${t(`${G}.${item.key}.short`)}`.toLowerCase().includes(q),
        );
    }, [items, search, t]);

    const current = selected ? items.find((item) => item.key === selected) ?? null : null;

    return (
        <Dialog open={open} onClose={close} className="relative z-[70]">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-[1px] dark:bg-black/70" aria-hidden="true" />
            <div className="fixed inset-0 flex items-center justify-center p-3 sm:p-6">
                <DialogPanel className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-transparent bg-white shadow-2xl dark:border-zinc-700 dark:bg-zinc-900">
                    {current ? (
                        <GuideView
                            item={current}
                            onBack={() => setSelected(null)}
                            onClose={close}
                            onRun={() => run(current)}
                            onOpenGuide={(key) => setSelected(key)}
                        />
                    ) : (
                        <>
                            <div className="flex items-start gap-4 px-6 pt-6">
                                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-violet-50 text-violet-600 dark:bg-violet-950/50 dark:text-violet-300">
                                    <PiGearBold className="h-6 w-6" />
                                </span>
                                <div className="min-w-0 flex-1">
                                    <DialogTitle className="text-lg font-bold text-gray-900 dark:text-zinc-100">
                                        {t(`${G}.modal_title`)}
                                    </DialogTitle>
                                    <p className="mt-0.5 text-sm text-gray-500 dark:text-zinc-400">{t(`${G}.modal_subtitle`)}</p>
                                </div>
                                <CloseButton onClick={close} />
                            </div>

                            <div className="px-6 pt-5">
                                <label className="relative block">
                                    <PiMagnifyingGlassBold className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                                    <input
                                        autoFocus
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        placeholder={t(`${G}.search_ph`)}
                                        className="w-full rounded-xl border border-violet-200 bg-violet-50/30 py-2.5 pl-10 pr-3 text-sm text-gray-900 outline-none transition placeholder:text-gray-400 focus:border-violet-400 focus:ring-2 focus:ring-violet-200 dark:border-violet-900/60 dark:bg-zinc-950 dark:text-zinc-100 dark:focus:ring-violet-900/50"
                                    />
                                </label>
                                <p className="mt-2 flex items-center gap-1.5 text-xs text-gray-500 dark:text-zinc-400">
                                    <PiInfoBold className="h-3.5 w-3.5 shrink-0" />
                                    {t(`${G}.hint`, { icon: 'ⓘ' })}
                                </p>
                            </div>

                            <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
                                {filtered.length === 0 ? (
                                    <p className="py-10 text-center text-sm text-gray-500 dark:text-zinc-400">{t(`${G}.no_results`)}</p>
                                ) : (
                                    <div className="grid gap-6 md:grid-cols-3 md:gap-0 md:divide-x md:divide-gray-100 dark:md:divide-zinc-800">
                                        {COLUMNS.map((groups, col) => (
                                            <div key={col} className={cn('space-y-5', col > 0 && 'md:pl-5', col < 2 && 'md:pr-5')}>
                                                {groups.map((group) => {
                                                    const groupItems = filtered.filter((item) => item.group === group);
                                                    if (groupItems.length === 0) return null;

                                                    return (
                                                        <section key={group}>
                                                            <h3 className="mb-2.5 text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-zinc-400">
                                                                {t(`${G}.group_${group}`)}
                                                            </h3>
                                                            <div className="space-y-2.5">
                                                                {groupItems.map((item) => (
                                                                    <ActionCard
                                                                        key={item.key}
                                                                        item={item}
                                                                        onRun={() => run(item)}
                                                                        onAbout={() => setSelected(item.key)}
                                                                    />
                                                                ))}
                                                            </div>
                                                        </section>
                                                    );
                                                })}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 px-6 py-4 dark:border-zinc-800">
                                <p className="flex items-center gap-2 text-xs text-gray-500 dark:text-zinc-400">
                                    <PiShieldCheckBold className="h-4 w-4 shrink-0" />
                                    {t(`${G}.footer_safe`)}
                                </p>
                                <div className="flex items-center gap-2">
                                    {onHistory && (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                close();
                                                onHistory();
                                            }}
                                            className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-gray-600 transition hover:bg-gray-100 dark:text-zinc-300 dark:hover:bg-zinc-800"
                                        >
                                            <PiClockCounterClockwiseBold className="h-4 w-4" />
                                            {t(`${G}.history`)}
                                        </button>
                                    )}
                                    <SecondaryButton onClick={close}>{t(`${G}.close`)}</SecondaryButton>
                                </div>
                            </div>
                        </>
                    )}
                </DialogPanel>
            </div>
        </Dialog>
    );
}

function ActionCard({ item, onRun, onAbout }: { item: CommissionActionItem; onRun: () => void; onAbout: () => void }) {
    const { t } = useI18n();
    const Icon = item.icon;
    const danger = item.tone === 'danger';

    return (
        <div
            role="button"
            tabIndex={item.disabled ? -1 : 0}
            aria-disabled={item.disabled}
            onClick={onRun}
            onKeyDown={(e) => {
                if (e.key === 'Enter') onRun();
            }}
            className={cn(
                'group flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-3 text-left shadow-sm transition dark:border-zinc-800 dark:bg-zinc-900',
                item.disabled
                    ? 'cursor-not-allowed opacity-50'
                    : 'cursor-pointer hover:border-violet-300 hover:bg-violet-50/40 hover:shadow dark:hover:border-violet-700 dark:hover:bg-violet-950/20',
            )}
        >
            <span
                className={cn(
                    'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
                    danger ? 'bg-rose-50 text-rose-600 dark:bg-rose-950/40 dark:text-rose-400' : 'bg-gray-50 text-gray-700 dark:bg-zinc-800 dark:text-zinc-200',
                )}
            >
                <Icon className="h-5 w-5" />
            </span>
            <div className="min-w-0 flex-1">
                <p className={cn('text-sm font-semibold', danger ? 'text-rose-600 dark:text-rose-400' : 'text-gray-900 dark:text-zinc-100')}>
                    {item.title}
                </p>
                <p className="mt-0.5 text-xs leading-snug text-gray-500 dark:text-zinc-400">{t(`${G}.${item.key}.short`)}</p>
            </div>
            <button
                type="button"
                title={t(`${G}.about`)}
                aria-label={t(`${G}.about`)}
                onClick={(e) => {
                    e.stopPropagation();
                    onAbout();
                }}
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-violet-500 transition hover:bg-violet-100 hover:text-violet-700 dark:text-violet-300 dark:hover:bg-violet-900/40"
            >
                <PiInfoBold className="h-4 w-4" />
            </button>
            <PiCaretRightBold className="h-4 w-4 shrink-0 text-gray-400 transition group-hover:translate-x-0.5 group-hover:text-violet-500" />
        </div>
    );
}

function GuideView({
    item,
    onBack,
    onClose,
    onRun,
    onOpenGuide,
}: {
    item: CommissionActionItem;
    onBack: () => void;
    onClose: () => void;
    onRun: () => void;
    onOpenGuide: (key: CommissionActionGuideKey) => void;
}) {
    const { t } = useI18n();
    const guide: Guide = GUIDES[item.key];
    const base = `${G}.${item.key}`;
    const Icon = item.icon;
    const danger = item.tone === 'danger';
    const hasSide = guide.side !== 'none';
    const listTitle = guide.kind === 'review' ? t(`${G}.review`) : t(`${G}.inform`);
    const descOf = (key: string) => {
        const value = t(key);
        return value === key ? null : value;
    };

    return (
        <>
            <div className="flex items-center justify-between px-6 pt-5">
                <button
                    type="button"
                    onClick={onBack}
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-violet-600 transition hover:text-violet-800 dark:text-violet-300"
                >
                    <PiArrowLeftBold className="h-4 w-4" />
                    {t(`${G}.back_to_actions`)}
                </button>
                <CloseButton onClick={onClose} />
            </div>

            <div className="flex items-start gap-4 px-6 pt-4">
                <span
                    className={cn(
                        'flex h-14 w-14 shrink-0 items-center justify-center rounded-xl',
                        danger ? 'bg-rose-50 text-rose-600 dark:bg-rose-950/40 dark:text-rose-400' : 'bg-violet-50 text-violet-600 dark:bg-violet-950/50 dark:text-violet-300',
                    )}
                >
                    <Icon className="h-7 w-7" />
                </span>
                <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                        <DialogTitle className="text-2xl font-bold text-gray-900 dark:text-zinc-100">{item.title}</DialogTitle>
                        <span className="rounded-full bg-violet-100 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-violet-700 dark:bg-violet-900/50 dark:text-violet-200">
                            {t(`${G}.group_${item.group}`)}
                        </span>
                    </div>
                    <p className="mt-1 text-sm text-gray-500 dark:text-zinc-400">{t(`${G}.understand`)}</p>
                </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
                <div className={cn('grid gap-5', hasSide && 'lg:grid-cols-[1.15fr_1fr]')}>
                    <div className="divide-y divide-gray-100 dark:divide-zinc-800">
                        <GuideSection icon={PiGearBold} title={t(`${G}.what`)}>
                            <p>{t(`${base}.what`)}</p>
                        </GuideSection>
                        <GuideSection icon={PiUsersBold} title={t(`${G}.when`)}>
                            <p>{t(`${base}.when`)}</p>
                        </GuideSection>
                        <GuideSection icon={PiFileTextBold} title={listTitle}>
                            <div className="mt-1 space-y-2">
                                {Array.from({ length: guide.list }, (_, i) => {
                                    const desc = descOf(`${base}.l${i + 1}_desc`);
                                    return guide.kind === 'review' ? (
                                        <div key={i} className="flex items-start gap-3 rounded-xl border border-gray-200 px-3.5 py-2.5 dark:border-zinc-800">
                                            <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-violet-100 text-violet-600 dark:bg-violet-900/50 dark:text-violet-300">
                                                <PiCheckBold className="h-3.5 w-3.5" />
                                            </span>
                                            <div>
                                                <p className="text-sm font-semibold text-gray-900 dark:text-zinc-100">{t(`${base}.l${i + 1}`)}</p>
                                                {desc && <p className="text-xs text-gray-500 dark:text-zinc-400">{desc}</p>}
                                            </div>
                                        </div>
                                    ) : (
                                        <div key={i} className="flex items-center gap-3 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 dark:border-zinc-800 dark:text-zinc-200">
                                            <PiCaretRightBold className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                                            {t(`${base}.l${i + 1}`)}
                                        </div>
                                    );
                                })}
                            </div>
                        </GuideSection>
                    </div>

                    {guide.side === 'example' && <ScheduleExample />}

                    {guide.side === 'risks' && (
                        <div className="rounded-2xl border border-rose-200 bg-rose-50/60 p-4 dark:border-rose-900/50 dark:bg-rose-950/20">
                            <p className="flex items-center gap-2 font-semibold text-rose-700 dark:text-rose-300">
                                <PiWarningBold className="h-5 w-5" />
                                {t(`${G}.risks_title`)}
                            </p>
                            <div className="mt-3 space-y-2.5">
                                {Array.from({ length: guide.risks }, (_, i) => (
                                    <div key={i} className="flex items-start gap-3 rounded-xl bg-white p-3 shadow-sm dark:bg-zinc-900">
                                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-rose-50 text-rose-600 dark:bg-rose-950/50 dark:text-rose-400">
                                            <PiWarningCircleBold className="h-5 w-5" />
                                        </span>
                                        <div>
                                            <p className="text-sm font-semibold text-gray-900 dark:text-zinc-100">{t(`${base}.s${i + 1}`)}</p>
                                            <p className="text-xs text-gray-500 dark:text-zinc-400">{t(`${base}.s${i + 1}_desc`)}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <p className="mt-3 text-[11px] text-rose-700/70 dark:text-rose-300/70">{t(`${G}.risks_footer`)}</p>
                        </div>
                    )}
                </div>

                {guide.tip === 'hold' && (
                    <div className="mt-5 flex flex-wrap items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 dark:border-amber-900/50 dark:bg-amber-950/20">
                        <PiLightbulbBold className="h-5 w-5 shrink-0 text-amber-500" />
                        <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold text-gray-900 dark:text-zinc-100">{t(`${G}.tip_hold_title`)}</p>
                            <p className="text-xs text-gray-600 dark:text-zinc-400">{t(`${G}.tip_hold_text`)}</p>
                        </div>
                        <button
                            type="button"
                            onClick={() => onOpenGuide('hold')}
                            className="inline-flex items-center gap-1 text-sm font-semibold text-violet-600 hover:text-violet-800 dark:text-violet-300"
                        >
                            {t(`${G}.tip_hold_link`)}
                            <PiArrowRightBold className="h-3.5 w-3.5" />
                        </button>
                    </div>
                )}

                {guide.before && (
                    <div className="mt-5 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 dark:border-amber-900/50 dark:bg-amber-950/20">
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-600 dark:bg-amber-900/40 dark:text-amber-300">
                            <PiWarningCircleBold className="h-5 w-5" />
                        </span>
                        <div>
                            <p className="text-sm font-semibold text-gray-900 dark:text-zinc-100">{t(`${G}.before_title`)}</p>
                            <p className="text-xs text-gray-600 dark:text-zinc-400">{t(`${base}.before`)}</p>
                        </div>
                    </div>
                )}
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 px-6 py-4 dark:border-zinc-800">
                <p className="flex items-center gap-2 text-xs text-gray-500 dark:text-zinc-400">
                    <PiShieldCheckBold className="h-4 w-4 shrink-0" />
                    {t(`${G}.explanatory_only`)}
                </p>
                <div className="flex items-center gap-2">
                    <SecondaryButton onClick={onBack}>{t(`${G}.back`)}</SecondaryButton>
                    <button
                        type="button"
                        disabled={item.disabled}
                        onClick={onRun}
                        className={cn(
                            'inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition disabled:opacity-50',
                            danger
                                ? 'border border-rose-300 bg-white text-rose-600 hover:bg-rose-50 dark:border-rose-800 dark:bg-zinc-900 dark:text-rose-400'
                                : 'bg-violet-600 text-white shadow-sm hover:bg-violet-700',
                        )}
                    >
                        {t(`${base}.go`)}
                        <PiArrowRightBold className="h-4 w-4" />
                    </button>
                </div>
            </div>
        </>
    );
}

function GuideSection({ icon: Icon, title, children }: { icon: IconType; title: string; children: React.ReactNode }) {
    return (
        <div className="flex gap-4 py-4 first:pt-0 last:pb-0">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-gray-200 text-gray-700 dark:border-zinc-700 dark:text-zinc-200">
                <Icon className="h-5 w-5" />
            </span>
            <div className="min-w-0 flex-1 text-sm leading-relaxed text-gray-600 dark:text-zinc-300">
                <p className="mb-1 text-base font-semibold text-gray-900 dark:text-zinc-100">{title}</p>
                {children}
            </div>
        </div>
    );
}

const EXAMPLE_DATES = ['2027-01-30', '2027-02-28', '2027-03-30', '2027-04-30', '2027-05-30'];

function ScheduleExample() {
    const { t } = useI18n();
    const fmtDate = (iso: string) => iso.split('-').reverse().join('/');
    const block = (title: string, count: number, amount: number) => (
        <div className="rounded-xl bg-white p-4 shadow-sm dark:bg-zinc-900">
            <div className="flex items-start gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-gray-200 text-gray-600 dark:border-zinc-700 dark:text-zinc-300">
                    <PiCalendarCheckBold className="h-5 w-5" />
                </span>
                <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-gray-900 dark:text-zinc-100">{title}</p>
                    <p className="text-sm text-gray-700 dark:text-zinc-300">
                        {t(`${G}.example_installments`, { count, amount: formatBrl(amount) })}
                    </p>
                    <div className="mt-2 space-y-0.5 text-xs text-gray-600 dark:text-zinc-400">
                        {EXAMPLE_DATES.slice(0, count).map((date, i) => (
                            <div key={date} className="grid grid-cols-[1fr_auto_auto] gap-4">
                                <span>{t(`${G}.example_parcel`, { n: i + 1 })}</span>
                                <span className="tabular-nums">{fmtDate(date)}</span>
                                <span className="tabular-nums">{formatBrl(amount)}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="rounded-2xl bg-violet-50/70 p-4 dark:bg-violet-950/20">
            <div className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white text-violet-600 shadow-sm dark:bg-zinc-900 dark:text-violet-300">
                    <PiChartBarFill className="h-5 w-5" />
                </span>
                <div>
                    <p className="font-semibold text-gray-900 dark:text-zinc-100">{t(`${G}.example_title`)}</p>
                    <p className="text-xs text-gray-500 dark:text-zinc-400">{t(`${G}.example_subtitle`)}</p>
                </div>
            </div>
            <div className="mt-3 space-y-2">
                {block(t(`${G}.example_current`), 4, 5000)}
                <div className="flex justify-center text-violet-500">
                    <PiArrowDownBold className="h-4 w-4" />
                </div>
                {block(t(`${G}.example_new`), 5, 4000)}
                <div className="rounded-xl border border-violet-200 bg-white px-4 py-2.5 text-center text-sm text-violet-700 dark:border-violet-900/60 dark:bg-zinc-900 dark:text-violet-300">
                    {t(`${G}.example_total`)} <span className="text-base font-bold">{formatBrl(20000)}</span>
                </div>
                <p className="text-center text-[11px] text-gray-500 dark:text-zinc-400">{t(`${G}.example_demo`)}</p>
            </div>
        </div>
    );
}

function CloseButton({ onClick }: { onClick: () => void }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className="rounded-lg p-1.5 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-zinc-800 dark:hover:text-zinc-300"
        >
            <PiX className="h-5 w-5" />
        </button>
    );
}

function SecondaryButton({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:bg-zinc-800"
        >
            {children}
        </button>
    );
}
